import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import StrategyKPICharts from "../../strategy/components/strategy/StrategyKPICharts";
import { calcRiskOnlyScore, getRiskLevel } from "utils/riskScore";

function BeastResults({ jobData, onReset }) {
  const [expandedIteration, setExpandedIteration] = useState(null);

  // 🆕 格式化執行時間
  const formatExecutionTime = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}min`;
  };

  if (!jobData || !jobData.beastiteration) {
    return null;
  }

  const iterations = jobData.beastiteration || [];
  
  // 找出最佳策略
  const bestIteration = jobData.bestIterationId
    ? iterations.find((iter) => iter.id === jobData.bestIterationId)
    : null;

  // 成功的迭代
  const successfulIterations = iterations.filter(
    (iter) => iter.status === "DONE" && iter.kpi
  );

  // 失敗的迭代
  const failedIterations = iterations.filter((iter) => iter.status === "FAILED");

  // 檢查是否有命中目標
  const hasHit = bestIteration?.isHit || false;

  // 解析 KPI 目標
  const renderKpiTarget = (kpiName, config) => {
    if (!config || !config.enabled) return null;

    const kpiLabels = {
      returnRatePct: '報酬率',
      maxDrawdownPct: '最大回撤',
      trades: '交易次數',
      profitLossRatio: '賺賠比',
      profitAmount: '獲利金額',
      winRate: '勝率',
    };

    const label = kpiLabels[kpiName] || kpiName;
    const unit = kpiName.includes('Pct') || kpiName === 'winRate' ? '%' : 
                 kpiName === 'profitAmount' ? ' USD' : 
                 kpiName === 'trades' ? ' 次' : '';

    let targetText = '';
    if (config.type === 'gt') {
      targetText = `> ${config.value}${unit}`;
    } else if (config.type === 'lt') {
      targetText = `< ${config.value}${unit}`;
    } else if (config.type === 'range') {
      targetText = `${config.min}${unit} ~ ${config.max}${unit}`;
    }

    return (
      <MDBox key={kpiName} display="flex" justifyContent="space-between" py={0.5}>
        <MDTypography variant="body2" color="text">
          {label}:
        </MDTypography>
        <MDTypography variant="body2" fontWeight="medium">
          {targetText}
        </MDTypography>
      </MDBox>
    );
  };

  const tradeDirectionLabel = {
    LONG: '僅做多',
    SHORT: '僅做空',
    AUTO: 'AI 自行判斷（可雙向）',
  };

  return (
    <MDBox>
      <Divider />
      
      {/* 標題和操作按鈕 */}
      <MDBox mt={3} display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h5">Beast AI 策略優化結果</MDTypography>
        <MDButton variant="outlined" color="info" onClick={onReset}>
          建立新任務
        </MDButton>
      </MDBox>

      {/* 1. 任務配置參數 */}
      <Card sx={{ mb: 3, p: 3 }}>
        <MDTypography variant="h6" mb={2}>
          📋 任務配置參數
        </MDTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MDBox p={2} borderRadius="md" bgColor="grey-100">
              <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
                基本設定
              </MDTypography>
              <MDBox display="flex" justifyContent="space-between" py={0.5}>
                <MDTypography variant="body2" color="text">交易品種:</MDTypography>
                <MDTypography variant="body2" fontWeight="medium">{jobData.symbol}</MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" py={0.5}>
                <MDTypography variant="body2" color="text">時間週期:</MDTypography>
                <MDTypography variant="body2" fontWeight="medium">{jobData.frequency}</MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" py={0.5}>
                <MDTypography variant="body2" color="text">回測期間:</MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {jobData.startYear || 'auto'} ~ {jobData.endYear || 'auto'}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" py={0.5}>
                <MDTypography variant="body2" color="text">初始資金:</MDTypography>
                <MDTypography variant="body2" fontWeight="medium">${jobData.capital}</MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" py={0.5}>
                <MDTypography variant="body2" color="text">交易方向:</MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {tradeDirectionLabel[jobData.tradeDirection] || jobData.tradeDirection}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" py={0.5}>
                <MDTypography variant="body2" color="text">最大迭代次數:</MDTypography>
                <MDTypography variant="body2" fontWeight="medium">{jobData.maxIterate}</MDTypography>
              </MDBox>
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <MDBox p={2} borderRadius="md" bgColor="grey-100">
              <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
                預期 KPI 目標
              </MDTypography>
              {jobData.kpiTargets && typeof jobData.kpiTargets === 'object' ? (
                Object.entries(jobData.kpiTargets).map(([kpiName, config]) => 
                  renderKpiTarget(kpiName, config)
                )
              ) : (
                <MDTypography variant="body2" color="text">無特定 KPI 要求</MDTypography>
              )}
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      {/* 2. 命中狀態提示 */}
      {!hasHit && bestIteration && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>未完全命中目標 KPI</AlertTitle>
          以下顯示的是 <strong>{iterations.length} 次迭代中表現最佳的策略</strong>，雖未完全達到所有目標 KPI，但在當前條件下已是最優解。
          您可以考慮：
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            <li>調整 KPI 目標範圍</li>
            <li>增加迭代次數</li>
            <li>修改交易方向限制</li>
          </ul>
        </Alert>
      )}

      {hasHit && bestIteration && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
          <AlertTitle>🎉 成功命中目標！</AlertTitle>
          恭喜！AI 已找到符合所有 KPI 目標的策略。
        </Alert>
      )}

      {/* 3. 最佳策略詳情 */}
      {bestIteration && (
        <Card sx={{ mb: 3, p: 3 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <MDTypography variant="h6" color={hasHit ? "success" : "warning"}>
                {hasHit ? "🏆 最佳策略（已命中目標）" : "⭐ 最佳策略（未完全命中）"} - Iteration #{bestIteration.iterNo}
              </MDTypography>
              {(() => {
                // 2026-09-22 採用方案 B：與下方 StrategyKPICharts 的「風險」卡同一把尺、同一份資料，
                //   否則同一張卡上方寫「54 中風險」(舊公式)、下方寫「86 高風險」(方案 B)。
                //   優先用 raw 的 performance_metrics（圖表吃的就是它）；bestIteration.kpi 的 camelCase
                //   欄位在 beastEngine 端會把缺值填成 0（＝看起來最安全），只當最後退路。
                const rawReport = bestIteration.kpi?.raw;
                const pm = (rawReport?.formattedResult || rawReport)?.performance_metrics;
                const score = calcRiskOnlyScore(pm || bestIteration.kpi);
                const info  = getRiskLevel(score);
                if (!info) return null;
                const chipColor = info.color === "success" ? "success"
                                : info.color === "warning" ? "warning"
                                : "error";
                return (
                  <Chip
                    label={`${info.label}　${score} 分`}
                    color={chipColor}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                );
              })()}
            </MDBox>
          </MDBox>

          {/* KPI 卡片 */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={2}>
              <MDBox textAlign="center" p={2} borderRadius="md" bgColor="success" variant="gradient">
                <MDTypography variant="h5" color="white">
                  {bestIteration.kpi?.returnRatePct?.toFixed(2) || 'N/A'}%
                </MDTypography>
                <MDTypography variant="caption" color="white">總回報率</MDTypography>
              </MDBox>
            </Grid>

            <Grid item xs={6} md={2}>
              <MDBox textAlign="center" p={2} borderRadius="md" bgColor="error" variant="gradient">
                <MDTypography variant="h5" color="white">
                  {bestIteration.kpi?.maxDrawdownPct?.toFixed(2) || 'N/A'}%
                </MDTypography>
                <MDTypography variant="caption" color="white">最大回撤</MDTypography>
              </MDBox>
            </Grid>

            <Grid item xs={6} md={2}>
              <MDBox textAlign="center" p={2} borderRadius="md" bgColor="info" variant="gradient">
                <MDTypography variant="h5" color="white">
                  {bestIteration.kpi?.trades || 'N/A'}
                </MDTypography>
                <MDTypography variant="caption" color="white">交易次數</MDTypography>
              </MDBox>
            </Grid>

            <Grid item xs={6} md={2}>
              <MDBox textAlign="center" p={2} borderRadius="md" bgColor="warning" variant="gradient">
                <MDTypography variant="h5" color="white">
                  {bestIteration.kpi?.profitLossRatio?.toFixed(2) || 'N/A'}
                </MDTypography>
                <MDTypography variant="caption" color="white">賺賠比</MDTypography>
              </MDBox>
            </Grid>

            <Grid item xs={6} md={2}>
              <MDBox textAlign="center" p={2} borderRadius="md" bgColor="dark" variant="gradient">
                <MDTypography variant="h5" color="white">
                  {bestIteration.kpi?.winRate?.toFixed(2) || 'N/A'}%
                </MDTypography>
                <MDTypography variant="caption" color="white">勝率</MDTypography>
              </MDBox>
            </Grid>

            <Grid item xs={6} md={2}>
              <MDBox textAlign="center" p={2} borderRadius="md" bgColor="secondary" variant="gradient">
                <MDTypography variant="h5" color="white">
                  ${bestIteration.kpi?.profitAmount?.toFixed(0) || 'N/A'}
                </MDTypography>
                <MDTypography variant="caption" color="white">獲利金額</MDTypography>
              </MDBox>
            </Grid>
          </Grid>

          {/* 策略說明 */}
          {bestIteration.dsl && (
            <>
              <Divider sx={{ my: 2 }} />
              <MDTypography variant="h6" mb={2}>
                📊 策略詳細說明
              </MDTypography>

              <MDBox p={2} borderRadius="md" bgColor="grey-100">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">策略名稱:</MDTypography>
                    <MDTypography variant="body2" mb={1}>{bestIteration.dsl.name || "N/A"}</MDTypography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">策略類型:</MDTypography>
                    <MDTypography variant="body2" mb={1}>{bestIteration.dsl.type || "N/A"}</MDTypography>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">使用的技術指標:</MDTypography>
                    <MDTypography variant="body2" mb={1}>{bestIteration.dsl.indicators || "N/A"}</MDTypography>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">進場條件:</MDTypography>
                    <MDTypography variant="body2" mb={1}>{bestIteration.dsl.entryLogic || "N/A"}</MDTypography>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">出場條件:</MDTypography>
                    <MDTypography variant="body2" mb={1}>{bestIteration.dsl.exitLogic || "N/A"}</MDTypography>
                  </Grid>

                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text" fontWeight="bold">風險管理:</MDTypography>
                    <MDTypography variant="body2">{bestIteration.dsl.riskManagement || "N/A"}</MDTypography>
                  </Grid>
                </Grid>
              </MDBox>
            </>
          )}

          {/* KPI 圖表 */}
          {bestIteration.kpi?.raw && (
            <>
              <Divider sx={{ my: 3 }} />
              <MDTypography variant="h6" mb={2}>
                📈 績效圖表分析
              </MDTypography>
              <StrategyKPICharts data={bestIteration.kpi.raw} />
            </>
          )}
        </Card>
      )}

      {/* 4. 所有迭代結果列表 */}
      <MDBox mt={3}>
        <MDTypography variant="h6" mb={2}>
          📝 所有迭代結果 ({iterations.length} 次)
        </MDTypography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>迭代</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell align="right">執行時間</TableCell>
                <TableCell align="right">回報率 (%)</TableCell>
                <TableCell align="right">最大回撤 (%)</TableCell>
                <TableCell align="right">交易次數</TableCell>
                <TableCell align="right">勝率 (%)</TableCell>
                <TableCell>策略名稱</TableCell>
                <TableCell align="center">詳情</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {iterations.map((iter) => (
                <TableRow 
                  key={iter.id} 
                  sx={{ 
                    backgroundColor: iter.id === jobData.bestIterationId 
                      ? "rgba(76, 175, 80, 0.1)" 
                      : "transparent" 
                  }}
                >
                  <TableCell>
                    <MDBox display="flex" alignItems="center">
                      #{iter.iterNo}
                      {iter.id === jobData.bestIterationId && (
                        <Chip label="最佳" color="success" size="small" sx={{ ml: 1 }} />
                      )}
                    </MDBox>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={iter.status === "DONE" ? <CheckCircleIcon /> : <CancelIcon />}
                      label={iter.status === "DONE" ? "成功" : "失敗"}
                      color={iter.status === "DONE" ? "success" : "error"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <MDTypography variant="caption" color="text">
                      {formatExecutionTime(iter.executionTimeMs)}
                    </MDTypography>
                  </TableCell>
                  <TableCell align="right">
                    <MDBox display="flex" alignItems="center" justifyContent="flex-end">
                      {iter.kpi?.returnRatePct ? (
                        <>
                          {iter.kpi.returnRatePct >= 0 ? (
                            <TrendingUpIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                          ) : (
                            <TrendingDownIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                          )}
                          {iter.kpi.returnRatePct.toFixed(2)}
                        </>
                      ) : "-"}
                    </MDBox>
                  </TableCell>
                  <TableCell align="right">
                    {iter.kpi?.maxDrawdownPct?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell align="right">{iter.kpi?.trades || "-"}</TableCell>
                  <TableCell align="right">
                    {iter.kpi?.winRate?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell>
                    <MDTypography variant="caption">
                      {iter.dsl?.name || (iter.error ? `錯誤: ${iter.error.substring(0, 30)}...` : "-")}
                    </MDTypography>
                  </TableCell>
                  <TableCell align="center">
                    {iter.status === "DONE" && iter.kpi?.raw && (
                      <MDButton
                        variant="text"
                        color="info"
                        size="small"
                        onClick={() => setExpandedIteration(expandedIteration === iter.id ? null : iter.id)}
                      >
                        <ShowChartIcon fontSize="small" />
                      </MDButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 展開的迭代詳情 */}
        {expandedIteration && iterations.find(i => i.id === expandedIteration)?.kpi?.raw && (
          <MDBox mt={2}>
            <Accordion expanded={true}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <MDTypography variant="h6">
                  Iteration #{iterations.find(i => i.id === expandedIteration)?.iterNo} 詳細圖表
                </MDTypography>
              </AccordionSummary>
              <AccordionDetails>
                <StrategyKPICharts data={iterations.find(i => i.id === expandedIteration)?.kpi?.raw} />
              </AccordionDetails>
            </Accordion>
          </MDBox>
        )}
      </MDBox>

      {/* 5. 統計摘要 */}
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2, textAlign: "center" }}>
            <MDTypography variant="h4" color="success">
              {successfulIterations.length}
            </MDTypography>
            <MDTypography variant="caption">成功迭代</MDTypography>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2, textAlign: "center" }}>
            <MDTypography variant="h4" color="error">
              {failedIterations.length}
            </MDTypography>
            <MDTypography variant="caption">失敗迭代</MDTypography>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2, textAlign: "center" }}>
            <MDTypography variant="h4" color="info">
              {iterations.length > 0 
                ? ((successfulIterations.length / iterations.length) * 100).toFixed(0)
                : 0}%
            </MDTypography>
            <MDTypography variant="caption">成功率</MDTypography>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ p: 2, textAlign: "center" }}>
            <MDTypography variant="h4" color={hasHit ? "success" : "warning"}>
              {hasHit ? "✓" : "✗"}
            </MDTypography>
            <MDTypography variant="caption">目標命中</MDTypography>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default BeastResults;
